import React, { useState, useMemo } from 'react';

export default function ReportTable({ data, columns }) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(row =>
      columns.some(col => {
        const val = row[col.key];
        return val != null && String(val).toLowerCase().includes(q);
      })
    );
  }, [data, search, columns]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortKey], bVal = b[sortKey];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number') return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      return sortDir === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filtered, sortKey, sortDir]);

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function formatCell(value, col) {
    if (value == null) return '—';
    if (col.format === 'currency') return '$' + Number(value).toLocaleString();
    if (col.format === 'percent') {
      const color = value > 0 ? '#cf1322' : value < 0 ? '#389e0d' : '#666';
      return <span style={{ color, fontWeight: 600 }}>{value > 0 ? '+' : ''}{value}%</span>;
    }
    if (col.format === 'date') return new Date(value).toLocaleDateString();
    if (col.format === 'badge') {
      const colors = {
        'Bound': { bg: '#f6ffed', fg: '#389e0d' },
        'Quote Sent': { bg: '#e6f7ff', fg: '#096dd9' },
        'Pending Review': { bg: '#fffbe6', fg: '#d48806' },
        'Needs Remarketing': { bg: '#fff2f0', fg: '#cf1322' },
        'Awaiting Client': { bg: '#fffbe6', fg: '#d48806' },
        'Follow-Up Needed': { bg: '#fff2f0', fg: '#cf1322' },
        'Proposal Sent': { bg: '#e6f7ff', fg: '#096dd9' },
        'Non-Payment': { bg: '#fff2f0', fg: '#cf1322' },
        'Insured Request': { bg: '#fffbe6', fg: '#d48806' },
        'Carrier Non-Renewal': { bg: '#f9f0ff', fg: '#722ed1' },
        'Underwriting': { bg: '#e6f7ff', fg: '#096dd9' },
        'Audit Non-Compliance': { bg: '#fff2f0', fg: '#cf1322' },
      };
      const c = colors[value] || { bg: '#f5f5f5', fg: '#666' };
      return <span style={{ background: c.bg, color: c.fg, padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</span>;
    }
    if (col.format === 'days') {
      const color = value <= 5 ? '#cf1322' : value <= 10 ? '#d48806' : '#389e0d';
      return <span style={{ color, fontWeight: 700 }}>{value} days</span>;
    }
    return String(value);
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.toolbar}>
        <input
          style={styles.searchInput}
          type="text"
          placeholder="🔍  Search records..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span style={styles.count}>{sorted.length} record{sorted.length !== 1 ? 's' : ''}</span>
      </div>
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  {sortKey === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={columns.length} style={styles.empty}>No records found</td></tr>
            ) : (
              sorted.map((row, i) => (
                <tr key={row.id || i} style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  {columns.map(col => (
                    <td key={col.key} style={styles.td}>{formatCell(row[col.key], col)}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  wrapper: { marginTop: '16px' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  searchInput: { padding: '10px 16px', border: '1px solid #d9d9d9', borderRadius: '8px', fontSize: '14px', width: '300px', outline: 'none' },
  count: { fontSize: '13px', color: '#999' },
  tableContainer: { overflowX: 'auto', borderRadius: '8px', border: '1px solid #e8e8e8' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { background: '#fafafa', padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #e8e8e8', whiteSpace: 'nowrap', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: '10px 16px', borderBottom: '1px solid #f0f0f0', color: '#333' },
  rowEven: { background: '#fff' },
  rowOdd: { background: '#fafafa' },
  empty: { textAlign: 'center', padding: '40px', color: '#999' },
};
